/**
 * sbDomain — SB-Portal Subdomain-Routing (Juni 2026).
 *
 * Das Steuerberater-Portal läuft auf einer eigenen Subdomain:
 *   PROD: https://sb.ivoliq.io      (Hauptdomain: ivoliq.io)
 *   DEV:  https://dev.sb.ivoliq.io  (Hauptdomain: dev.ivoliq.io)
 *
 * In der Preview-Umgebung (emergentagent.com) und auf localhost gibt es KEINE
 * Subdomain — alle Helfer verhalten sich dort neutral (relative Pfade, keine
 * Redirects), damit Preview & Tests unverändert funktionieren.
 *
 * SB-Routen: exakt "/steuerberater" (Login inkl. ?verify=…) + alles unter "/advisor".
 * NICHT SB: "/steuerberater-bereich" (FL-Seite) und "/steuerberater-portal" (SEO/Marketing).
 */

// Hauptdomain → SB-Subdomain
const SB_HOSTS = {
  "ivoliq.io": "sb.ivoliq.io",
  "www.ivoliq.io": "sb.ivoliq.io",
  "dev.ivoliq.io": "dev.sb.ivoliq.io",
};

// SB-Subdomain → Hauptdomain
const MAIN_HOSTS = {
  "sb.ivoliq.io": "ivoliq.io",
  "dev.sb.ivoliq.io": "dev.ivoliq.io",
};

export const isSbHost = () => Boolean(MAIN_HOSTS[window.location.hostname]);

/** Absolute SB-Portal-URL (PROD/DEV) bzw. relativer Pfad (Preview/localhost/SB-Host). */
export const sbUrl = (path) => {
  const sb = SB_HOSTS[window.location.hostname];
  return sb ? `https://${sb}${path}` : path;
};

/** Absolute Hauptdomain-URL, wenn wir auf der SB-Subdomain sind — sonst relativer Pfad. */
export const mainUrl = (path) => {
  const main = MAIN_HOSTS[window.location.hostname];
  return main ? `https://${main}${path}` : path;
};

const isSbPath = (pathname) => pathname === "/steuerberater" || pathname.startsWith("/advisor");

/**
 * Erzwingt die Domain-Trennung (wird bei jeder Navigation aufgerufen):
 *  - SB-Host: "/" → "/steuerberater"; alle Nicht-SB-Routen → Hauptdomain
 *  - Hauptdomain (nur PROD/DEV): SB-Routen → SB-Subdomain
 * Gibt true zurück, wenn ein Redirect ausgelöst wurde.
 */
/**
 * Synchroner Bootstrap-Guard — wird EINMAL vor dem ersten React-Render
 * (in index.js) aufgerufen, um Screen-Flackern auf der SB-Subdomain zu
 * verhindern (sonst blitzt die FL-Root-Route auf, bevor der useEffect-Redirect
 * feuert).
 *  - SB-Host "/"        → wird per history.replaceState zu "/steuerberater"
 *                         umgeschrieben (gleiche Origin, KEIN Reload, kein Flash).
 *  - SB-Host Nicht-SB   → Cross-Origin-Redirect zur Hauptdomain (skip render).
 *  - Hauptdomain + SB   → Cross-Origin-Redirect zur SB-Subdomain (skip render).
 * Rückgabe: true = harter Redirect ausgelöst → React NICHT rendern.
 */
export function prepareInitialDomain() {
  const host = window.location.hostname;
  const { pathname, search } = window.location;
  const main = MAIN_HOSTS[host];
  if (main) {
    if (pathname === "/") {
      window.history.replaceState({}, "", `/steuerberater${search}`);
      return false;
    }
    if (!isSbPath(pathname)) {
      window.location.replace(`https://${main}${pathname}${search}`);
      return true;
    }
    return false;
  }
  const sb = SB_HOSTS[host];
  if (sb && isSbPath(pathname)) {
    window.location.replace(`https://${sb}${pathname}${search}`);
    return true;
  }
  return false;
}

export function enforceSbDomain(pathname, search = "") {
  const host = window.location.hostname;
  const main = MAIN_HOSTS[host];
  if (main) {
    // Wir sind auf der SB-Subdomain
    if (pathname === "/") {
      window.location.replace("/steuerberater");
      return true;
    }
    if (!isSbPath(pathname)) {
      window.location.replace(`https://${main}${pathname}${search}`);
      return true;
    }
    return false;
  }
  const sb = SB_HOSTS[host];
  if (sb && isSbPath(pathname)) {
    window.location.replace(`https://${sb}${pathname}${search}`);
    return true;
  }
  return false;
}
